import React from 'react';
import { Card, Form, Input, DatePicker, Select, Button, Space, Row, Col, InputNumber } from 'antd';
import { Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TaskTimelog } from '../../services/tasks';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface User {
  id: number;
  name: string;
  email: string;
}

interface OverviewTabProps {
  form: any;
  isEditing: boolean;
  customerType: 'personal' | 'business';
  customers: any[];
  businesses: any[];
  taskTypes: any[];
  taskTemplates: any[];
  quotations: any[];
  timelogs: TaskTimelog[];
  isEdit: boolean;
  id?: string;
  handleCustomerTypeChange: (type: 'personal' | 'business') => void;
  handleCustomerChange: (customerId: number) => void;
  handleBusinessChange: (businessId: number) => void;
  handleTaskTemplateChange: (templateId: number | null) => void;
  handleQuotationChange: (quotationId: number) => void;
  handleCreatedDateChange: (date: dayjs.Dayjs | null) => void;
  getDisplayValue: (fieldName: string, type: 'select' | 'date' | 'number' | 'text', options?: any[]) => string;
  calculateTotalHours: () => number;
  timelogForm: { date: dayjs.Dayjs; hour: number; description: string };
  setTimelogForm: React.Dispatch<React.SetStateAction<{ date: dayjs.Dayjs; hour: number; description: string }>>;
  handleAddTimelog: () => void;
  handleDeleteTimelog: (timelogId: number | string) => void;
  user?: User;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  form,
  isEditing,
  customerType,
  customers,
  businesses,
  taskTypes,
  taskTemplates,
  quotations,
  timelogs,
  isEdit,
  handleCustomerTypeChange,
  handleCustomerChange,
  handleBusinessChange,
  handleTaskTemplateChange,
  handleQuotationChange,
  handleCreatedDateChange,
  getDisplayValue,
  calculateTotalHours,
  timelogForm,
  setTimelogForm,
  handleAddTimelog,
  handleDeleteTimelog,
  user,
}) => {
  const navigate = useNavigate();
  return (
    <Row gutter={24}>
      {/* Left Column */}
      <Col span={12}>
        <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {/* Customer Type - 单独一行，合并后三列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Customer</strong>
                </td>
                <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Space>
                      <Button
                        type={customerType === 'personal' ? 'primary' : 'default'}
                        onClick={() => handleCustomerTypeChange('personal')}
                        size="small"
                      >
                        Personal
                      </Button>
                      <Button
                        type={customerType === 'business' ? 'primary' : 'default'}
                        onClick={() => handleCustomerTypeChange('business')}
                        size="small"
                      >
                        Business
                      </Button>
                    </Space>
                  ) : (
                    <div>
                      {customerType === 'personal' ? 'Personal' : 'Business'}
                    </div>
                  )}
                </td>
              </tr>
              {/* Customer/Business - 单独一行，合并后三列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                </td>
                <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isEditing ? (
                    customerType === 'personal' ? (
                      <Form.Item
                        name="customer_id"
                        rules={[{ required: true, message: 'Please select customer' }]}
                        style={{ margin: 0 }}
                      >
                        <Select
                          placeholder="Select Customer"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          onChange={handleCustomerChange}
                        >
                          {customers.map((customer) => (
                            <Select.Option key={customer.id} value={customer.id}>
                              {customer.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name="business_id"
                        rules={[{ required: true, message: 'Please select business' }]}
                        style={{ margin: 0 }}
                      >
                        <Select
                          placeholder="Select Business"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          onChange={handleBusinessChange}
                        >
                          {businesses.map((business) => (
                            <Select.Option key={business.id} value={business.id}>
                              {business.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )
                  ) : (
                    <div>
                      {customerType === 'personal' 
                        ? getDisplayValue('customer_id', 'select', customers)
                        : getDisplayValue('business_id', 'select', businesses)
                      }
                    </div>
                  )}
                </td>
              </tr>
              {/* Type 和 Year - 同一行，各占两列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Type</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="task_type_id" style={{ margin: 0 }}>
                      <Select placeholder="Select Type" allowClear>
                        {taskTypes.map((type) => (
                          <Select.Option key={type.id} value={type.id}>
                            {type.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('task_type_id', 'select', taskTypes)}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Year</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="year" style={{ margin: 0 }}>
                      <Select placeholder="Select Year" allowClear>
                        {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                          <Select.Option key={year} value={year}>
                            {year}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('year', 'text')}
                    </div>
                  )}
                </td>
              </tr>
              {/* Task - 单独一行，合并后三列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Task</strong>
                </td>
                <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="task_template_id" style={{ margin: 0 }}>
                      <Select
                        placeholder="Select Task"
                        allowClear
                        onChange={handleTaskTemplateChange}
                      >
                        {taskTemplates.map((template) => (
                          <Select.Option key={template.id} value={template.id}>
                            {template.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('task_template_id', 'select', taskTemplates)}
                    </div>
                  )}
                </td>
              </tr>
              {/* Create 和 Due - 同一行，各占两列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Create</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="created_date" style={{ margin: 0 }}>
                      <DatePicker 
                        style={{ width: '100%' }} 
                        format="DD/MM/YYYY" 
                        placeholder="Enter Create Date"
                        onChange={handleCreatedDateChange}
                      />
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('created_date', 'date')}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Due</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="due_date" style={{ margin: 0 }}>
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Due Date" />
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('due_date', 'date')}
                    </div>
                  )}
                </td>
              </tr>
              {/* Plan 和 Deadline - 同一行，各占两列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Plan</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="plan_date" style={{ margin: 0 }}>
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Plan Date" />
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('plan_date', 'date')}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Deadline</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="deadline" style={{ margin: 0 }}>
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Deadline" />
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('deadline', 'date')}
                    </div>
                  )}
                </td>
              </tr>
              {/* Estimate 和 Actual - 同一行，各占两列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Estimate</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="plan_hour" style={{ margin: 0 }}>
                      <InputNumber style={{ width: '100%' }} placeholder="Enter Estimate Hour" min={0} />
                    </Form.Item>
                  ) : (
                    <div>
                      {getDisplayValue('plan_hour', 'number')}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Actual</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  <div>
                    {calculateTotalHours() || '-'}
                  </div>
                </td>
              </tr>
              {/* Quotation # 和 Invoice # - 同一行，各占两列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Quotation #</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  {isEditing ? (
                    <Form.Item name="quotation_id" style={{ margin: 0 }}>
                      <Select
                        placeholder="Select Quotation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        onChange={handleQuotationChange}
                      >
                        {quotations.map((quotation) => (
                          <Select.Option key={quotation.id} value={quotation.id}>
                            {quotation.quotation_number}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    <div>
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          const quotationId = getFieldValue('quotation_id');
                          const quotation = quotations.find(q => q.id === quotationId);
                          if (!quotation?.quotation_number) {
                            return '-';
                          }

                          if (!quotation?.id) {
                            return quotation.quotation_number;
                          }

                          return (
                            <Button
                              type="link"
                              onClick={() => navigate(`/quotations/${quotation.id}/edit`)}
                              style={{ padding: 0, height: 'auto' }}
                            >
                              {quotation.quotation_number}
                            </Button>
                          );
                        }}
                      </Form.Item>
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Invoice #</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  <div>
                    <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.quotation_id !== currentValues.quotation_id} noStyle>
                      {({ getFieldValue }) => {
                        const quotationId = getFieldValue('quotation_id');
                        const quotation = quotations.find(q => q.id === quotationId);
                        if (!quotation?.invoice?.invoice_number) {
                          return '-';
                        }

                        if (!quotation?.invoice?.id) {
                          return quotation.invoice.invoice_number;
                        }

                        return (
                          <Button
                            type="link"
                            onClick={() => navigate(`/invoices/${quotation.invoice.id}/edit`)}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            {quotation.invoice.invoice_number}
                          </Button>
                        );
                      }}
                    </Form.Item>
                  </div>
                </td>
              </tr>
              {/* Team 和 Staff - 同一行，各占两列 */}
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Team</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  <div>
                    <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.customer_id !== currentValues.customer_id} noStyle>
                      {({ getFieldValue }) => {
                        const customerId = getFieldValue('customer_id');
                        const customer = customers.find(c => c.id === customerId);
                        return customer?.team?.name || '-';
                      }}
                    </Form.Item>
                  </div>
                </td>
                <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Staff</strong>
                </td>
                <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                  <div>
                    <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.quotation_id !== currentValues.quotation_id} noStyle>
                      {({ getFieldValue }) => {
                        const quotationId = getFieldValue('quotation_id');
                        const quotation = quotations.find(q => q.id === quotationId);
                        return quotation?.staff?.name || '-';
                      }}
                    </Form.Item>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </Col>

      {/* Right Column */}
      <Col span={12}>
        {/* Actual Timelog */}
        <Card 
          title="Actual" 
          bodyStyle={{ padding: '16px' }}
        >
          {/* Actual Timelogs List */}
          <div style={{ marginBottom: 24 }}>
            {timelogs.length === 0 ? (
              <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                No timelogs yet
              </div>
            ) : (
              timelogs.map((timelog) => (
                <div 
                  key={timelog.id} 
                  style={{ 
                    padding: '12px 0',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                  }}
                >
                  <Space>
                    <span>{timelog.hour}h</span>
                    <span>{new Date(timelog.date).toLocaleDateString()}</span>
                    <span>{timelog.description || 'Description'}</span>
                  </Space>
                  {isEdit && (
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteTimelog(timelog.id)}
                      style={{ padding: 0 }}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Add Timelog Section */}
          <div style={{ paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ marginBottom: 12, fontWeight: 500, color: '#595959' }}>Add Timelog</div>
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col span={12}>
                <div style={{ marginBottom: 4, color: '#595959', fontSize: '12px' }}>Date</div>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Enter Date"
                  value={timelogForm.date}
                  onChange={(date) => setTimelogForm(prev => ({ ...prev, date: date || dayjs() }))}
                />
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4, color: '#595959', fontSize: '12px' }}>Hour</div>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter Hour"
                  min={0}
                  value={timelogForm.hour}
                  onChange={(hour) => setTimelogForm(prev => ({ ...prev, hour: hour || 0 }))}
                />
              </Col>
            </Row>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, color: '#595959', fontSize: '12px' }}>Description</div>
              <Input
                placeholder="Enter Description"
                value={timelogForm.description}
                onChange={(e) => setTimelogForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTimelog}
              style={{ width: '100%' }}
            >
              Add Timelog
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  );
};